import { LitElement, html, css, unsafeCSS } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import './theme-toggle.js'
import { apolloClient } from '../utils/apollo.js'
import { getSelectedLanguageId, setSelectedLanguageId, SUPPORTED_LANGUAGES } from '../utils/language.js'
import { t } from '../utils/i18n.js'
// Import Material Web Components
import '@material/web/button/filled-button.js'
import '@material/web/select/filled-select.js'
import '@material/web/select/select-option.js'
import '@tlenexkoyotl/xamix-element'
import styles from './app-header.css?inline'

/**
 * Application header component
 */
@customElement('app-header')
export class AppHeader extends LitElement {
  @state() private isLoggedIn = false
  @state() private selectedLanguageId = getSelectedLanguageId()
  @state() private mobileMenuOpen = false

  connectedCallback() {
    super.connectedCallback()
    this.checkLoginStatus()
    window.addEventListener('resize', this.handleResize)
    // Listen for storage changes (login/logout in other tabs)
    window.addEventListener('storage', () => this.checkLoginStatus())
    // Listen for custom auth-changed event (login/logout in this tab)
    window.addEventListener('auth-changed', () => this.checkLoginStatus())
    // Listen for popstate (route changes that might indicate login)
    window.addEventListener('popstate', () => this.checkLoginStatus())
    // Listen for language changes
    window.addEventListener('language-changed', (e: Event) => {
      const customEvent = e as CustomEvent<{ languageId: string }>
      this.selectedLanguageId = customEvent.detail.languageId
      this.requestUpdate()
    })
    // Listen for i18n updates
    window.addEventListener('i18n-updated', () => {
      this.requestUpdate()
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback?.()
    window.removeEventListener('resize', this.handleResize)
  }

  private handleResize = () => {
    if (window.innerWidth > 768 && this.mobileMenuOpen) {
      this.mobileMenuOpen = false
    }
  }

  private checkLoginStatus() {
    const token = localStorage.getItem('auth-token')
    this.isLoggedIn = !!token
  }

  private handleLogout() {
    localStorage.removeItem('auth-token')
    // Clear Apollo Client cache
    apolloClient.clearStore()
    // Dispatch custom event to notify of logout
    window.dispatchEvent(new CustomEvent('auth-changed', { detail: { loggedIn: false } }))
    // Redirect to home page
    window.history.pushState({}, '', '/')
    window.dispatchEvent(new PopStateEvent('popstate'))
  }

  private handleLanguageChange(e: Event) {
    const select = e.target as any
    const value = select.value || select.selected?.value
    if (value) {
      setSelectedLanguageId(value)
      this.selectedLanguageId = value
    }
  }

  private toggleMobileMenu() {
    this.mobileMenuOpen = !this.mobileMenuOpen
  }

  private closeMobileMenu() {
    this.mobileMenuOpen = false
  }
  static styles = css`${unsafeCSS(styles)}`

  render() {
    return html`
      ${this.mobileMenuOpen ? html`
        <div class="mobile-backdrop" @click=${this.closeMobileMenu} aria-hidden="true"></div>
      ` : ''}
      <header>
        <div class="header-left">
          <span><xamix-element text-input="tla'-tol-a-mox-tli" root="/node_modules/@tlenexkoyotl/xamix-element" font-size="3" bold adaptable></xamix-element></span>
          <h1>TlahtolAmoxtli</h1>
        </div>
        <button class="hamburger" aria-label="${this.mobileMenuOpen ? 'Close menu' : 'Open menu'}" @click=${this.toggleMobileMenu}>
          <span class="hamburger-line"></span>
          <span class="hamburger-line"></span>
          <span class="hamburger-line"></span>
        </button>
        <div class="header-right ${this.mobileMenuOpen ? 'open' : ''}">
          <nav>
            <a href="/" data-route="/" @click=${this.closeMobileMenu}>${t('nav.dictionary')}</a>
            <a href="/about" data-route="/about" @click=${this.closeMobileMenu}>${t('nav.about')}</a>
            ${this.isLoggedIn ? html`<a href="/create" data-route="/create" @click=${this.closeMobileMenu}>${t('nav.create')}</a>` : ''}
            ${this.isLoggedIn ? html`<a href="/edit" data-route="/edit" @click=${this.closeMobileMenu}>${t('nav.edit')}</a>` : ''}
            ${this.isLoggedIn ? html`<a href="/languages" data-route="/languages" @click=${this.closeMobileMenu}>${t('nav.languages')}</a>` : ''}
            ${this.isLoggedIn ? html`<a href="/users" data-route="/users" @click=${this.closeMobileMenu}>${t('nav.users')}</a>` : ''}
            ${!this.isLoggedIn ? html`<a href="/login" data-route="/login" @click=${this.closeMobileMenu}>${t('nav.login')}</a>` : ''}
            ${this.isLoggedIn ? html`<md-filled-button class="logout" @click=${this.handleLogout}>${t('nav.logout')}</md-filled-button>` : ''}
          </nav>
          <div class="header-utils">
            <md-filled-select 
              class="language-select" 
              label="Language"
              .value=${this.selectedLanguageId}
              @change=${this.handleLanguageChange}
            >
              ${SUPPORTED_LANGUAGES.map(lang => html`
                <md-select-option value=${lang.id} ?selected=${lang.id === this.selectedLanguageId}>
                  <div slot="headline">${lang.name}</div>
                </md-select-option>
              `)}
            </md-filled-select>
            <theme-toggle></theme-toggle>
          </div>
        </div>
      </header>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'app-header': AppHeader
  }
}
